import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardShell from "../../components/DashboardShell";
import { api } from "../../lib/api";
import { ArrowLeft, ShieldCheck, Copy, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const actionStyles = {
  allow: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  deny: "bg-red-500/15 text-red-300 border-red-500/30",
  redact: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  flag: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};

export default function DecisionDetail() {
  const { id } = useParams();
  const [evidence, setEvidence] = useState(null);
  const [ctg, setCtg] = useState(null);
  const [seer, setSeer] = useState(null);
  const [seerLoading, setSeerLoading] = useState(false);

  const runSeer = async () => {
    setSeerLoading(true);
    try {
      const { data } = await api.post(`/v1/seer/${id}`);
      setSeer(data.analysis);
      toast.success("Seer analysis ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Seer failed"); }
    finally { setSeerLoading(false); }
  };

  useEffect(() => {
    api.get(`/v1/evidence/${id}`).then(r => setEvidence(r.data));
    api.get(`/v1/observatory/ctg/${id}`).then(r => setCtg(r.data));
  }, [id]);

  const d = evidence?.decision;

  return (
    <DashboardShell
      title={d ? `Decision · ${d.action.toUpperCase()}` : "Decision"}
      subtitle={d?.id}
      actions={<><button data-testid="seer-run" onClick={runSeer} disabled={seerLoading} className="btn-primary text-xs">{seerLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>} Ask Seer</button><Link to="/app/decisions" className="btn-ghost-dark text-xs"><ArrowLeft size={12}/> Back</Link></>}
    >
      {!d ? <div className="text-cream/50">Loading…</div> : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="flex items-center justify-between mb-4">
                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-bold uppercase ${actionStyles[d.action]}`}>{d.action}</span>
                <button onClick={() => { navigator.clipboard.writeText(d.id); toast.success("Decision ID copied"); }} className="text-cream/50 text-xs flex gap-1 items-center hover:text-cream">
                  <Copy size={12}/> {d.id}
                </button>
              </div>
              <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>PROMPT</div>
              <pre data-testid="decision-prompt" className="font-mono text-sm text-cream/85 whitespace-pre-wrap bg-black/30 p-4 rounded-lg border border-white/5">{d.prompt}</pre>
              {d.response && <>
                <div className="eyebrow text-[#FFD98A] mt-4 mb-2" style={{fontSize:10}}>RESPONSE</div>
                <pre className="font-mono text-sm text-cream/85 whitespace-pre-wrap bg-black/30 p-4 rounded-lg border border-white/5">{d.response}</pre>
              </>}
              {d.redacted_prompt && <>
                <div className="eyebrow text-amber-400 mt-4 mb-2" style={{fontSize:10}}>REDACTED PROMPT</div>
                <pre className="font-mono text-sm text-amber-200 whitespace-pre-wrap bg-black/30 p-4 rounded-lg border border-amber-500/20">{d.redacted_prompt}</pre>
              </>}
            </div>

            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="eyebrow text-[#FFD98A] mb-3" style={{fontSize:10}}>POLICY CHAIN</div>
              {d.reasons?.length ? d.reasons.map((r,i) => (
                <div key={i} className="border border-white/10 rounded-lg p-3 mb-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-[#FF6B35]"/>
                    <div className="font-semibold">{r.policy_name}</div>
                    <span className="ml-auto chip chip-cream text-[10px]">{r.action}</span>
                  </div>
                  <div className="text-sm text-cream/70 mt-1">{r.reason}</div>
                  <div className="text-[11px] text-cream/40 font-mono mt-1">matched: {r.matched}</div>
                </div>
              )) : <div className="text-sm text-cream/60">No rule triggered. Request passed through.</div>}
            </div>

            {ctg && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-5">
                <div className="eyebrow text-[#FFD98A] mb-3" style={{fontSize:10}}>CAUSAL TRACE GRAPH</div>
                <div className="flex flex-wrap gap-2">
                  {ctg.nodes.map(n => (
                    <div key={n.id} className={`px-3 py-2 rounded-lg text-xs font-mono border ${n.id==="decision" ? "bg-[#FF6B35] text-white border-[#FF6B35]" : "bg-white/5 border-white/10"}`}>
                      <div className="text-[10px] opacity-60 uppercase">{n.type}</div>
                      <div className="font-semibold">{n.label}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-[11px] text-cream/50 font-mono space-y-0.5">
                  {ctg.edges.map((e,i)=><div key={i}>{e.from} —[{e.type}]→ {e.to}</div>)}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            {seer && (
              <div className="rounded-xl bg-gradient-to-br from-[#FF6B35]/20 to-[#FFD98A]/10 border border-[#FF6B35]/40 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[#FFD98A]"/>
                  <div className="eyebrow text-[#FFD98A]" style={{fontSize:10}}>SEER · AI ANALYSIS</div>
                  <span className="ml-auto chip chip-cream text-[10px] uppercase">{seer.risk_level}</span>
                </div>
                <div className="text-sm text-cream/90">{seer.root_cause}</div>
                {seer.suggested_policy && (
                  <>
                    <div className="eyebrow text-[#FFD98A] mt-3 mb-1" style={{fontSize:10}}>SUGGESTED POLICY</div>
                    <pre className="font-mono text-[11px] bg-black/40 p-2 rounded whitespace-pre-wrap text-cream/85">{seer.suggested_policy}</pre>
                  </>
                )}
                {seer.remediation?.length > 0 && (
                  <>
                    <div className="eyebrow text-[#FFD98A] mt-3 mb-1" style={{fontSize:10}}>REMEDIATION</div>
                    <ul className="text-xs text-cream/80 space-y-1 list-disc list-inside">
                      {seer.remediation.map((r,i) => <li key={i}>{r}</li>)}
                    </ul>
                  </>
                )}
              </div>
            )}
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="eyebrow text-[#FFD98A] mb-3" style={{fontSize:10}}>METADATA</div>
              {[
                ["Provider", d.provider],
                ["Model", d.model],
                ["User ref", d.user_ref || "—"],
                ["Latency", `${d.latency_ms?.toFixed(2)} ms`],
                ["Tokens in / out", `${d.tokens_in} / ${d.tokens_out}`],
                ["Cost", `$${d.cost_usd}`],
                ["Timestamp", new Date(d.timestamp).toLocaleString()],
              ].map(([k,v]) => (
                <div key={k} className="flex justify-between py-1.5 text-sm border-b border-white/5 last:border-0">
                  <span className="text-cream/50">{k}</span>
                  <span className="font-mono text-cream/85">{v}</span>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5">
              <div className="eyebrow text-[#FFD98A] mb-2" style={{fontSize:10}}>EVIDENCE HASH</div>
              <div className="font-mono text-[11px] text-cream/70 break-all">{d.sha256}</div>
            </div>
          </aside>
        </div>
      )}
    </DashboardShell>
  );
}
